import apiClient from './client'
import type {
  AuthToken,
  CreateTokenRequest,
  TokenResponse,
  ApiResponse,
} from '@/types/models'

/**
 * Canonical per-user API-key surface (authentication.md §3). rivulet v0.11.0+
 * serves `/api/v1/me/api-keys`; the older `/api/v1/auth/tokens*` routes are
 * kept by the API as aliases only.
 */
const API_KEYS_PATH = '/api/v1/me/api-keys'

/** Wire shape of one key in list responses (metadata only, never the secret). */
interface ApiKeyRow {
  id: string
  name: string
  prefix?: string
  legacy?: boolean
  last_used?: string | null
  /** Pre-v0.11 alias responses used this name. */
  last_used_at?: string | null
  expires_at?: string | null
  created_at?: string
}

/** Wire shape of the mint response: `token` is returned exactly once. */
interface ApiKeyMintRow extends ApiKeyRow {
  token: string
}

function toAuthToken(row: ApiKeyRow): AuthToken {
  return {
    id: row.id,
    name: row.name,
    prefix: row.prefix,
    legacy: row.legacy ?? false,
    expiresAt: row.expires_at ?? null,
    lastUsedAt: row.last_used ?? row.last_used_at ?? null,
    createdAt: row.created_at,
  }
}

/**
 * Auth API endpoints for API-key management
 */
export const authApi = {
  /**
   * Mint a new API key. The API takes an absolute `expires_at` (default 90
   * days server-side); the UI's "days" input is converted here.
   */
  async createToken(data: CreateTokenRequest): Promise<ApiResponse<TokenResponse>> {
    const apiRequest: { name: string; expires_at?: string } = { name: data.name }
    if (data.expiresInDays && data.expiresInDays > 0) {
      apiRequest.expires_at = new Date(
        Date.now() + data.expiresInDays * 24 * 60 * 60 * 1000
      ).toISOString()
    }
    const response = await apiClient.post<ApiKeyMintRow>(API_KEYS_PATH, apiRequest)
    return {
      data: {
        id: response.id,
        token: response.token,
        name: response.name,
        prefix: response.prefix,
        expiresAt: response.expires_at ?? null,
        createdAt: response.created_at,
      },
    }
  },

  /**
   * List all API keys for the current user. The canonical route answers a bare
   * array; the legacy alias wrapped it in `{tokens: [...]}` — accept both.
   */
  async listTokens(): Promise<ApiResponse<AuthToken[]>> {
    const response = await apiClient.get<ApiKeyRow[] | { tokens?: ApiKeyRow[] }>(API_KEYS_PATH)
    const rows = Array.isArray(response) ? response : response?.tokens ?? []
    return { data: rows.map(toAuthToken) }
  },

  /**
   * Revoke an API key by ID
   */
  async revokeToken(id: string): Promise<void> {
    return apiClient.delete(`${API_KEYS_PATH}/${encodeURIComponent(id)}`)
  },
}

export default authApi
